import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageHeader from '../../../components/owner/PageHeader';
import GymIdMemberSearch from '../../../components/receptionist/GymIdMemberSearch';
import {
  ColumnFilterHeader,
  useColumnFilters,
} from '../../../components/admin/ColumnFilterHeader';
import PrintActionsHeader from '../../../components/shared/PrintActionsHeader';
import { usePanelToast } from '../../../context/PanelToastContext';
import { getGymMemberId } from '../../admin/members/memberData';
import { formatCurrency } from '../receptionistData';
import {
  fetchMembersPage,
  fetchMembershipsForMember,
  fetchRenewalSalePage,
  createPayment,
  updateMembership,
  createMembership,
  deletePayment,
} from '../../../services/receptionist/receptionistApi';
import {
  ensureWalkInMember,
  isWalkInMember,
  WALK_IN_GYM_MEMBER_ID,
  WALK_IN_PAYMENT_NAME,
} from '../../../services/receptionist/walkInMember';
import { notifyPaymentsUpdated } from '../paymentStore';
import { dedupeMembers } from '../../../utils/dedupeList';
import TablePagination from '../../../components/shared/TablePagination';
import {
  combineMethodLabels,
  defaultPaymentSplits,
  serializePaymentSplits,
} from '../../../utils/splitPaymentUtils';
import { MEMBER_SEARCH_LABEL, MEMBER_SEARCH_PLACEHOLDER } from './salePageConfig';
import SaleAmountFields from './SaleAmountFields';
import SaleDurationField from './SaleDurationField';
import SaleReceiptModal from './SaleReceiptModal';
import PayBalanceModal from './PayBalanceModal';
import RemainingClearHistory from './RemainingClearHistory';
import SaleHistoryRowActions from './SaleHistoryRowActions';
import SaleEditModal from './SaleEditModal';
import { buildSaleReceiptData, buildReceiptFromPaymentRow } from './buildSaleReceipt';
import {
  SALE_DURATION_VALUES,
  addDurationToDate,
  dayAfterDate,
  emptySalePeriodFields,
  formatDurationLabel,
  saleEndDate,
  syncSalePeriodFields,
} from './saleDurationUtils';
import { saleRowStartDate, saleRowEndDate, saleRowDuration, saleRowPackageName } from './salePeriodUtils';
import {
  formatDateFilterValue,
  saleHistoryFilterDefaults,
} from './saleHistoryFilters';
import { createSaleHistoryExporter } from './exportSaleHistory';
import { isReceptionistPanelScope, RECEPTIONIST_BRANCH_EVENT } from '../../../services/gym/branchContext';
import { pkTodayRange } from '../../../utils/pkTime';
import { useSaleWriteBranch, SaleBranchField } from '../../../hooks/useSaleWriteBranch';
import {
  SaleMethodColumnHeaders,
  SaleMethodCells,
  SALE_METHOD_COLUMN_COUNT,
} from './SaleMethodTableColumns';
import { resolveSalePayment, saleRowLeft, saleRowPaid, saleRowTotal } from './salePaymentUtils';
import '../../owner/ownerPages.css';
import '../../owner/subscriptions/subscriptions.css';
import '../members/members.css';
import '../sales.css';
import './ancillarySales.css';

function saleDate(row) {
  return String(row.date || '').slice(0, 10) || '—';
}

const HISTORY_COL_COUNT = 9 + SALE_METHOD_COLUMN_COUNT + 3;

function todayDate() {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function emptyForm() {
  const today = todayDate();
  return {
    ...emptySalePeriodFields(today),
    amount: '',
    paymentSplits: defaultPaymentSplits(),
    date: today,
    note: '',
    complimentary: false,
  };
}

function membershipForMember(member, memberships) {
  if (!member?.id) return null;
  const rows = (memberships || [])
    .filter((m) => m.memberId === member.id)
    .sort((a, b) => String(b.expiry || '').localeCompare(String(a.expiry || '')));
  return rows[0] || null;
}

export default function RenewalSalePage() {
  const toast = usePanelToast();
  const splitsTouchedRef = useRef(false);
  const [memberHits, setMemberHits] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [sales, setSales] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const historyPageSize = 50;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState({ name: '', gymId: '', member: null });
  const {
    writeBranch,
    branches: writeBranches,
    pickedBranch,
    setPickedBranch,
    showPicker: showBranchPicker,
  } = useSaleWriteBranch(selected.member, selected.gymId);
  const [form, setForm] = useState(emptyForm);
  const [receipt, setReceipt] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [saved, setSaved] = useState(false);
  const receptionistScope = isReceptionistPanelScope();
  const todayRange = pkTodayRange();
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const listRangeStart = receptionistScope ? todayRange.startDate : rangeStart;
  const listRangeEnd = receptionistScope ? todayRange.endDate : rangeEnd;
  const [collected, setCollected] = useState(0);
  /** Bumps when receptionist branch finishes syncing — history must reload (avoids permanent 0 sales). */
  const [branchTick, setBranchTick] = useState(0);
  const {
    filters,
    updateFilter,
    openFilterKey,
    toggleFilter,
  } = useColumnFilters(saleHistoryFilterDefaults());
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilters(filters), 300);
    return () => clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    const onBranch = () => {
      setHistoryPage(1);
      setBranchTick((n) => n + 1);
    };
    window.addEventListener(RECEPTIONIST_BRANCH_EVENT, onBranch);
    return () => window.removeEventListener(RECEPTIONIST_BRANCH_EVENT, onBranch);
  }, []);

  const saleExporter = useMemo(
    () => createSaleHistoryExporter({
      title: 'Renewal Sale',
      itemLabel: 'Renewal',
      slug: 'renewal-sale',
    }),
    [],
  );

  const loadSales = useCallback(async () => {
    try {
      setError('');
      setLoading(true);
      const result = await fetchRenewalSalePage({
        page: historyPage,
        pageSize: historyPageSize,
        filters: {
          ...debouncedFilters,
          rangeStart: listRangeStart,
          rangeEnd: listRangeEnd,
        },
      });
      setSales(result.rows || []);
      setHistoryTotal(Number(result.total) || 0);
      setCollected(Number(result.collected) || 0);
    } catch (err) {
      setError(err.message || 'Failed to load renewal data');
      setSales([]);
      setHistoryTotal(0);
      setCollected(0);
    } finally {
      setLoading(false);
    }
  }, [historyPage, historyPageSize, listRangeStart, listRangeEnd, debouncedFilters, branchTick]);

  useEffect(() => {
    loadSales();
  }, [loadSales]);

  useEffect(() => {
    setHistoryPage(1);
  }, [listRangeStart, listRangeEnd, debouncedFilters]);

  useEffect(() => {
    let cancelled = false;
    const q = query.trim();
    if (!q) {
      setMemberHits(selected.member ? [selected.member] : []);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const result = await fetchMembersPage({ search: q, pageSize: 25 });
        if (!cancelled) setMemberHits(dedupeMembers(result.rows || []));
      } catch {
        if (!cancelled) setMemberHits([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, selected.member]);

  useEffect(() => {
    let cancelled = false;
    const memberId = selected.member?.id;
    if (!memberId) {
      setMemberships([]);
      return undefined;
    }
    (async () => {
      try {
        const rows = await fetchMembershipsForMember(memberId);
        if (!cancelled) setMemberships(rows || []);
      } catch {
        if (!cancelled) setMemberships([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selected.member?.id]);

  const currentMembership = useMemo(
    () => membershipForMember(selected.member, memberships),
    [selected.member, memberships],
  );

  const renewalPreview = useMemo(() => {
    const today = todayDate();
    const currentExpiry = String(currentMembership?.expiry || '').slice(0, 10);
    const periodStart = form.startDate || form.date || today;
    const membershipExtendFrom = currentExpiry && currentExpiry >= today
      ? dayAfterDate(currentExpiry)
      : periodStart;
    const periodEnd = form.endDate || addDurationToDate(periodStart, form.duration);
    return {
      duration: form.duration,
      durationLabel: formatDurationLabel(form.duration),
      membershipExtendFrom,
      startDate: periodStart,
      periodEnd,
      newExpiry: addDurationToDate(membershipExtendFrom, form.duration),
      packageName: currentMembership?.package || 'Membership Renewal',
    };
  }, [form.duration, form.startDate, form.endDate, form.date, currentMembership]);

  const filteredSales = sales;

  const historyTotalPages = Math.max(1, Math.ceil(historyTotal / historyPageSize) || 1);
  const historyFrom = historyTotal === 0 ? 0 : (historyPage - 1) * historyPageSize + 1;
  const historyTo = Math.min(historyPage * historyPageSize, historyTotal);
  const totalCollected = useMemo(() => {
    const pagePaid = filteredSales.reduce((sum, row) => sum + saleRowPaid(row), 0);
    return collected || pagePaid;
  }, [filteredSales, collected]);

  /** Print/export: one RPC call (p_page=0), not a 500-page loop. */
  const handleExport = async (exporterFn) => {
    try {
      const result = await fetchRenewalSalePage({
        filters: {
          ...debouncedFilters,
          rangeStart: listRangeStart,
          rangeEnd: listRangeEnd,
        },
        forExport: true,
      });
      const rows = result.rows || [];
      exporterFn(rows.length ? rows : filteredSales);
    } catch (err) {
      toast.showError(err.message || 'Export failed');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePeriodChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => syncSalePeriodFields(prev, { [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selected.member?.id) {
      toast.showError('Select a member added from Add Member (search by Serial No, name, phone, or CNIC).');
      return;
    }
    if (showBranchPicker && writeBranches.length > 0 && !writeBranch) {
      toast.showError('Select a branch for this sale.');
      return;
    }
    if (!SALE_DURATION_VALUES.includes(form.duration)) {
      toast.showError('Choose a valid duration.');
      return;
    }

    const complimentary = Boolean(form.complimentary);
    const amountBlank = String(form.amount ?? '').trim() === '';
    let payment = complimentary
      ? { total: 0, paid: 0, left: 0, status: 'complimentary' }
      : resolveSalePayment(amountBlank ? 0 : form.amount, form.paymentSplits);

    if (!complimentary) {
      if (!Number.isFinite(payment.total) || payment.total < 0) {
        toast.showError('Enter a valid total amount, or leave blank to set later.');
        return;
      }
      // 0 paid is allowed (pay later) — sale stays pending until edited with a payment
      if (!Number.isFinite(payment.paid) || payment.paid < 0) {
        toast.showError('Enter a valid paid amount (0 is allowed for pay later).');
        return;
      }
      if (amountBlank && payment.paid > 0) {
        payment = { total: payment.paid, paid: payment.paid, left: 0, status: 'completed' };
      } else if (payment.paid > payment.total) {
        toast.showError('Total paid cannot exceed the sale total.');
        return;
      }
    }

    try {
      setSaving(true);
      const member = selected.member;
      const walkInSelected = isWalkInMember(member) || selected.gymId === WALK_IN_GYM_MEMBER_ID;
      const memberName = String(selected.name || member?.name || '').trim()
        || (walkInSelected ? WALK_IN_PAYMENT_NAME : '');
      const gymId = String(selected.gymId || getGymMemberId(member) || '').trim()
        || (walkInSelected ? WALK_IN_GYM_MEMBER_ID : '');
      const durationLabel = formatDurationLabel(form.duration);
      const itemLabel = renewalPreview.packageName;
      const transactionDate = form.date || todayDate();
      const startDate = form.startDate || transactionDate;
      const endDate = form.endDate || saleEndDate(startDate, form.duration);
      const newExpiry = renewalPreview.newExpiry;

      if (currentMembership?.id) {
        await updateMembership(currentMembership.id, {
          expiry: newExpiry,
          status: 'active',
          package: currentMembership.package,
          duration: durationLabel,
          datesTbc: false,
          complimentary,
        });
      } else {
        await createMembership({
          memberId: member.id,
          packageName: itemLabel,
          expiryDate: newExpiry,
          startDate,
          status: 'active',
          duration: durationLabel,
          complimentary,
        });
      }

      const paymentId = complimentary ? '—' : `PAY-${String(Date.now()).slice(-6)}`;
      const durationNote = complimentary
        ? `Complimentary renewal ${durationLabel} (${startDate} to ${endDate}, membership to ${newExpiry})`
        : `Renewed ${durationLabel} (${startDate} to ${endDate}, membership to ${newExpiry})`;
      const paymentNote = [form.note, durationNote].filter(Boolean).join(' · ');

      let invoiceId = '';
      // Complimentary: extend membership only — do not create a payments row
      if (!complimentary) {
        try {
          const paymentSplits = serializePaymentSplits(form.paymentSplits);
          const saved = await createPayment({
            id: paymentId,
            paymentId,
            memberId: member.id,
            member: memberName,
            package: itemLabel,
            amount: payment.paid,
            packagePrice: payment.total,
            method: combineMethodLabels(form.paymentSplits),
            paymentSplits,
            date: transactionDate,
            status: payment.status,
            source: 'renewal_sale',
            paymentNote,
            gymId,
            startDate,
            endDate,
            duration: durationLabel,
            branch: writeBranch,
          });
          invoiceId = saved?.invoiceId || '';
        } catch (payErr) {
          const detail = payErr?.message || 'Payment insert failed';
          throw new Error(
            `Membership was updated but payment failed: ${detail}. Re-open Renewal and save payment, or check payments table columns.`,
          );
        }
      } else {
        // Complimentary: no payments row — do not burn a CR / invoice number
        invoiceId = '';
      }

      const receiptData = buildSaleReceiptData({
        saleTitle: 'Renewal Sale',
        saleLabel: 'Renewal Sale',
        customer: memberName,
        gymId,
        phone: selected.member?.phone || selected.member?.mobile || '',
        item: renewalPreview.packageName,
        duration: form.duration,
        startDate,
        endDate,
        transactionDate,
        totalAmount: payment.total,
        paymentAmount: payment.paid,
        totalLeft: payment.left,
        paymentSplits: complimentary ? [] : form.paymentSplits,
        paymentNote: form.note || (complimentary ? 'Complimentary renewal' : ''),
        status: payment.status,
        invoiceId,
        paymentId,
        complimentary,
      });

      notifyPaymentsUpdated();
      toast.showSaved(complimentary ? 'Complimentary renewal' : 'Renewal Sale');
      setReceipt(receiptData);
      setSaved(true);
      splitsTouchedRef.current = false;
      setForm(emptyForm());
      setQuery('');
      setSelected({ name: '', gymId: '', member: null });
      await loadSales();
    } catch (err) {
      toast.showError(err.message || 'Failed to save renewal');
    } finally {
      setSaving(false);
    }
  };

  const handleShowReceipt = (row) => {
    setReceipt(buildReceiptFromPaymentRow(row, { title: 'Renewal Sale', label: 'Renewal Sale' }));
  };

  const handleBalancePaid = async (receiptData) => {
    setReceipt(receiptData);
    await loadSales();
  };

  const handleDeleteSale = async () => {
    if (!deleteTarget || deleting) return;
    const paymentDbId = deleteTarget.rawId || deleteTarget.id;
    try {
      setDeleting(true);
      await deletePayment(paymentDbId);
      setDeleteTarget(null);
      notifyPaymentsUpdated();
      toast.showDeleted('Renewal Sale');
      await loadSales();
    } catch (err) {
      toast.showError(err.message || 'Failed to delete sale');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="rec-ancillary-sale">
      <PageHeader
        title="Renewal Sale"
        subtitle="Renew membership for members added from Add Member"
      >
        {saved && receipt && (
          <span className="owner-badge owner-badge-success">
            Renewal saved — {receipt.invoiceId}
          </span>
        )}
      </PageHeader>

      {loading && <p className="owner-placeholder-text owner-mb-16">Loading…</p>}
      {error && (
        <p className="owner-placeholder-text owner-mb-16" style={{ color: 'var(--owner-danger, #dc2626)' }}>
          {error}
        </p>
      )}

      <div className="owner-card rec-panel-card rec-ancillary-sale-form-card">
        <h2 className="owner-card-title">New Renewal Sale</h2>
        <form className="subscription-form rec-ancillary-sale-form" onSubmit={handleSubmit}>
          <div className="rec-ancillary-sale-grid">
            <div className="owner-form-group rec-ancillary-sale-full">
              <GymIdMemberSearch
                id="renewal-sale-member"
                label={MEMBER_SEARCH_LABEL}
                query={query}
                onQueryChange={setQuery}
                selectedName={selected.name}
                selectedGymId={selected.gymId}
                selectedMember={selected.member}
                onSelect={setSelected}
                members={memberHits}
                serverFiltered
                editableSelection
                placeholder={MEMBER_SEARCH_PLACEHOLDER}
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8, alignItems: 'center' }}>
                <button
                  type="button"
                  className="owner-btn owner-btn-secondary owner-btn-sm"
                  onClick={async () => {
                    try {
                      const walkIn = await ensureWalkInMember();
                      setQuery(WALK_IN_GYM_MEMBER_ID);
                      setSelected({
                        name: WALK_IN_PAYMENT_NAME,
                        gymId: WALK_IN_GYM_MEMBER_ID,
                        member: walkIn,
                      });
                      setMemberHits([walkIn]);
                    } catch (err) {
                      toast.showError(err.message || 'Could not load walk-in customer.');
                    }
                  }}
                >
                  Walk-In Customer
                </button>
                {selected.gymId === WALK_IN_GYM_MEMBER_ID && (
                  <span className="owner-placeholder-text" style={{ margin: 0, fontSize: 12 }}>
                    Walk-in selected (Serial {WALK_IN_GYM_MEMBER_ID})
                  </span>
                )}
              </div>
              {showBranchPicker && (
                <SaleBranchField
                  id="renewal-sale-branch"
                  branches={writeBranches}
                  value={pickedBranch}
                  onChange={setPickedBranch}
                />
              )}
              {query.trim() && !selected.member && !memberHits.length && (
                <p className="owner-placeholder-text" style={{ marginTop: 8, fontSize: 12 }}>
                  No members match this search.
                </p>
              )}
              {selected.member && currentMembership && (
                <p className="owner-placeholder-text" style={{ marginTop: 8, fontSize: 12 }}>
                  Current package: <strong>{currentMembership.package || '—'}</strong>
                  {' · '}
                  Expiry: <strong>{String(currentMembership.expiry || '').slice(0, 10) || '—'}</strong>
                  {' · '}
                  New expiry after renewal: <strong>{renewalPreview.newExpiry}</strong>
                  {' · '}
                  Sale period: <strong>{renewalPreview.startDate}</strong> to <strong>{renewalPreview.periodEnd}</strong>
                </p>
              )}
              {selected.member && !currentMembership && (
                <p className="owner-placeholder-text" style={{ marginTop: 8, fontSize: 12 }}>
                  No prior membership found — a new membership will be created through{' '}
                  <strong>{renewalPreview.newExpiry}</strong>.
                </p>
              )}
            </div>

            <SaleDurationField
              idPrefix="renewal-sale"
              duration={form.duration}
              startDate={form.startDate}
              endDate={form.endDate}
              onPeriodChange={handlePeriodChange}
            />

            <div className="owner-form-group rec-ancillary-sale-full">
              <label className="rec-sub-summary-check rec-add-package-tbc-check">
                <input
                  type="checkbox"
                  checked={Boolean(form.complimentary)}
                  onChange={(e) => setForm((prev) => ({
                    ...prev,
                    complimentary: e.target.checked,
                  }))}
                />
                <span>Complimentary — no payment required for this renewal</span>
              </label>
            </div>

            {form.complimentary ? (
              <p className="owner-placeholder-text rec-ancillary-sale-full" style={{ margin: 0 }}>
                Complimentary renewal — membership will be extended with no payment recorded.
              </p>
            ) : (
              <SaleAmountFields
                idPrefix="renewal-sale"
                totalAmount={form.amount}
                paymentSplits={form.paymentSplits}
                onTotalChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))}
                onPaymentSplitsChange={(paymentSplits) => setForm((prev) => ({ ...prev, paymentSplits }))}
                onSplitsTouched={() => { splitsTouchedRef.current = true; }}
              />
            )}

            <div className="owner-form-group">
              <label className="owner-form-label" htmlFor="renewal-date">Date</label>
              <input
                id="renewal-date"
                name="date"
                type="date"
                className="owner-form-input"
                value={form.date}
                onChange={handleChange}
                required
              />
              <p className="owner-placeholder-text rec-sale-payment-hint">
                {form.complimentary ? 'Renewal / transaction date.' : 'Payment / transaction date.'}
              </p>
            </div>

            <div className="owner-form-group rec-ancillary-sale-full">
              <label className="owner-form-label" htmlFor="renewal-note">Note</label>
              <input
                id="renewal-note"
                name="note"
                className="owner-form-input"
                value={form.note}
                onChange={handleChange}
                placeholder="Optional note"
              />
            </div>
          </div>

          <div className="subscription-form-actions">
            <button type="submit" className="owner-btn owner-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save Renewal Sale'}
            </button>
          </div>
        </form>
      </div>

      <div className="owner-card rec-panel-card rec-member-list-card">
        <div className="rec-ancillary-sale-history-header">
          <div>
            <h2 className="owner-card-title">
              Renewal Sale history
            </h2>
            <p className="owner-placeholder-text" style={{ margin: '6px 0 0' }}>
              {historyTotal} sales
              {historyTotal > 0 ? ` · showing ${historyFrom}–${historyTo}` : ''}
              {filteredSales.length ? ` · ${formatCurrency(totalCollected)}` : ''}
              {(listRangeStart || listRangeEnd) ? ` · ${listRangeStart || '…'} → ${listRangeEnd || '…'}` : ''}
            </p>
          </div>
        </div>
        <PrintActionsHeader
          className="print-actions-header--card"
          startDate={listRangeStart}
          endDate={listRangeEnd}
          onStartDateChange={setRangeStart}
          onEndDateChange={setRangeEnd}
          onClearDates={receptionistScope ? undefined : () => { setRangeStart(''); setRangeEnd(''); }}
          datesReadOnly={receptionistScope}
          onPrint={() => handleExport(saleExporter.print)}
          onCsv={() => handleExport(saleExporter.csv)}
          onPdf={() => handleExport(saleExporter.pdf)}
          onExcel={() => handleExport(saleExporter.excel)}
          onExport={() => handleExport(saleExporter.exportAll)}
          ariaLabel="Renewal sale print and export"
        />
        {receptionistScope ? (
          <p className="owner-placeholder-text owner-mb-16">
            Pakistan time · today only — renewals sold today
          </p>
        ) : null}
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Date"
                  filterKey="date"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="DD-MM-YYYY"
                  formatValue={formatDateFilterValue}
                />
                <ColumnFilterHeader
                  label="Serial No"
                  filterKey="gymId"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search serial..."
                />
                <ColumnFilterHeader
                  label="Member"
                  filterKey="member"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search member..."
                />
                <ColumnFilterHeader
                  label="Renewal"
                  filterKey="renewal"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search renewal..."
                />
                <ColumnFilterHeader
                  label="Duration"
                  filterKey="duration"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search duration..."
                />
                <SaleMethodColumnHeaders
                  sales={sales}
                  filters={filters}
                  openFilterKey={openFilterKey}
                  toggleFilter={toggleFilter}
                  updateFilter={updateFilter}
                />
                <ColumnFilterHeader
                  label="Total"
                  filterKey="total"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search total..."
                />
                <ColumnFilterHeader
                  label="Total paid"
                  filterKey="totalPaid"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search paid..."
                />
                <ColumnFilterHeader
                  label="Total left"
                  filterKey="totalLeft"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search left..."
                />
                <ColumnFilterHeader
                  label="Start date"
                  filterKey="startDate"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="DD-MM-YYYY"
                  formatValue={formatDateFilterValue}
                />
                <ColumnFilterHeader
                  label="End date"
                  filterKey="endDate"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="DD-MM-YYYY"
                  formatValue={formatDateFilterValue}
                />
                <ColumnFilterHeader
                  label="Note"
                  filterKey="note"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search note..."
                />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSales.length ? filteredSales.map((row) => (
                <tr key={row.rawId || row.id}>
                  <td>{saleDate(row)}</td>
                  <td className="rec-member-gym-id">{row.gymId || '—'}</td>
                  <td>{row.member || '—'}</td>
                  <td>{saleRowPackageName(row)}</td>
                  <td>{saleRowDuration(row)}</td>
                  <SaleMethodCells row={row} />
                  <td>{formatCurrency(saleRowTotal(row))}</td>
                  <td className="finance-amount-positive">{formatCurrency(saleRowPaid(row))}</td>
                  <td className={saleRowLeft(row) > 0 ? 'finance-amount-negative' : ''}>
                    {formatCurrency(saleRowLeft(row))}
                  </td>
                  <td>{saleRowStartDate(row)}</td>
                  <td>{saleRowEndDate(row)}</td>
                  <td>{row.paymentNote || '—'}</td>
                  <td>
                    <SaleHistoryRowActions
                      row={row}
                      onReceipt={handleShowReceipt}
                      onPayBalance={setPayTarget}
                      onEdit={setEditTarget}
                      onDelete={setDeleteTarget}
                    />
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={HISTORY_COL_COUNT} className="owner-placeholder-text">
                    No renewal sales yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <TablePagination
          page={historyPage}
          totalPages={historyTotalPages}
          total={historyTotal}
          from={historyFrom}
          to={historyTo}
          onPageChange={setHistoryPage}
          onPrev={() => setHistoryPage((p) => Math.max(1, p - 1))}
          onNext={() => setHistoryPage((p) => Math.min(historyTotalPages, p + 1))}
        />
      </div>

      <RemainingClearHistory title="Renewal — Remaining Clear" />

      <SaleReceiptModal receipt={receipt} onClose={() => setReceipt(null)} />
      {editTarget && (
        <SaleEditModal
          key={editTarget.rawId || editTarget.id}
          row={editTarget}
          title="Edit Renewal Sale"
          itemLabel="Renewal package"
          itemPlaceholder="e.g. Premium"
          onClose={() => setEditTarget(null)}
          onSaved={loadSales}
        />
      )}
      {payTarget && (
        <PayBalanceModal
          key={payTarget.rawId || payTarget.id}
          paymentRow={payTarget}
          saleMeta={{ title: 'Renewal Sale', label: 'Renewal Sale' }}
          onClose={() => setPayTarget(null)}
          onPaid={handleBalancePaid}
        />
      )}
      {deleteTarget && (
        <div className="subscription-modal-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="subscription-modal admin-form-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Delete Renewal Sale</h2>
              <button type="button" className="subscription-modal-close" onClick={() => setDeleteTarget(null)} aria-label="Close">×</button>
            </div>
            <p className="owner-placeholder-text" style={{ margin: '0 0 16px' }}>
              Delete renewal for <strong>{deleteTarget.member || 'this customer'}</strong>? This action cannot be undone.
            </p>
            <div className="subscription-form-actions">
              <button type="button" className="owner-btn owner-btn-secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</button>
              <button type="button" className="owner-btn rec-member-btn-delete" onClick={handleDeleteSale} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
